import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * React-router doesn't reset scroll position when the URL changes —
 * if a shopper scrolls to the bottom of `/shop` and then taps a card,
 * the PDP loads with `window.scrollY` still at the previous bottom,
 * which reads as "the page opened at the bottom".
 *
 * This component listens to every pathname change and snaps the
 * window back to (0, 0). Mount once near the top of the router tree.
 *
 * Bug ref: AI Agent — "When navigating to Cart, Checkout, or Order
 * Detail pages, the page auto-scrolls to the bottom instead of
 * loading from the top".
 */
export function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // `behavior: 'auto'` is intentional — we want an instant jump on
    // navigation, not a smooth animation. Smooth-scrolling-to-top
    // every page transition feels janky.
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    /* Defensive body-scroll reset — any modal / drawer / zoom view
       that locked `body.style.overflow = 'hidden'` and failed to
       clean up on unmount would otherwise leave the destination
       page stuck. Setting to empty string defers to whatever rule
       the stylesheet defined (usually `visible`).
       Bug ref: AI Agent — "Cart and Checkout pages are not
       scrolling properly". */
    if (document.body.style.overflow === 'hidden') {
      document.body.style.overflow = '';
    }
  }, [pathname]);
  return null;
}

export default ScrollToTop;
